import React, {useEffect, useState} from 'react';

import isEqual from 'lodash/isEqual';

import Autocomplete, {AutoCompleteBaseProps} from '@components/auto-complete-base';

const DEF_GET_OPTION_VALUE = option => option.value;
const DEF_OPTIONS = [];
const DEF_NO_OPTIONS_TEXT = 'No options';

export interface MultiSelectProps<T = any, V = string>
  extends Omit<AutoCompleteBaseProps<T>, 'value'> {
  value: Array<V>;
}

/**
 * MultiSelect with Auto-suggest and ability to create additional options on the fly
 *
 * Simplest usage is:
 *
 * import React, {useState} from 'react';
 * import MultiSelect from '@components/multi-select';
 *
 * const options = [
 *   {title: 'First Title', value: '1'},
 *   {title: 'Second Title', value: '2'},
 * ];
 *
 * const MyComponent = () => {
 *   const [value, setValue] = useState([]);
 *
 *   return (
 *     <MultiSelect
 *        options={options}
 *        value={value}
 *        onChange={setValue}
 *     />
 *   );
 * }
 */
export const MultiSelect = <T, V>({
  options = DEF_OPTIONS,
  getValue = DEF_GET_OPTION_VALUE,
  value,
  noOptionsText = DEF_NO_OPTIONS_TEXT,
  ...rest
}: MultiSelectProps<T, V>) => {
  const [preparedValue, setPreparedValue] = useState<T[]>([]);
  useEffect(() => {
    let newPreparedValue: T[] = [];
    if (Array.isArray(value)) {
      newPreparedValue = (options as T[]).filter(option => {
        return value.some(oneValue => isEqual(getValue(option), oneValue));
      });
    }
    if (!isEqual(newPreparedValue, preparedValue)) {
      setPreparedValue(newPreparedValue);
    }
  }, [getValue, options, preparedValue, value]);

  return (
    <Autocomplete<T, true>
      {...rest}
      disableCloseOnSelect
      multiple
      value={preparedValue}
      options={options}
      getValue={getValue}
      noOptionsText={noOptionsText}
    />
  );
};
